import { makeStyles } from "tss-react/mui"
import { Stack, TextField, Tooltip, Typography } from "@mui/material"

import { FilteredStatuses, MeetingServiceOrder, MeetingServiceOrderStatus } from "../meetingServiceTypes"
import { Box } from "@mui/system"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { OrdersStatusTable } from "./statusChangeModal/OrdersStatusTable"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/pro-light-svg-icons"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import {
    batchUpdateDeliveryStatus,
    MerchantMeetingServiceOrdersQuery,
    MeetingServiceOrdersResponse,
} from "../meetingServiceApi"
import { MerchantProductionTransactions } from "../../productionApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useRef } from "react"
import { enqueueSnackbar } from "notistack"
import { MerchantHomeQuery } from "../../../home/merchantHomeApi"
import { Order } from "../../../../controlKitchenTypes"
import { useQueryClient } from "@tanstack/react-query"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"

const useStyles = makeStyles()((theme) => ({
    container: {
        padding: "2rem",
    },
    messageSection: {
        maxWidth: "50%",
    },
    warningIcon: {
        color: theme.palette.tailwind.red[500],
        marginRight: theme.spacing(1),
    },
    changeButton: {
        width: "fit-content",
        alignSelf: "center",
    },
    messageReason: {
        fontSize: "0.75rem",
        color: theme.palette.tailwind.slate[400],
    },
}))

type StartPageLayoutProps = Readonly<{
    setShowUndo: (ordersChanged: number) => void
    onClose: () => void
    orders: MeetingServiceOrder[] | Order[]
    selectedStatus: FilteredStatuses
    allowedOrderIds: number[]
    meetingServiceDependencies?: any
    isTakeaway?: boolean
}>

export function StatusChangeModal({
    orders,
    selectedStatus,
    allowedOrderIds,
    setShowUndo,
    onClose,
    meetingServiceDependencies,
    isTakeaway,
}: StartPageLayoutProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const queryClient = useQueryClient()
    const fieldRef = useRef<HTMLInputElement>(null)
    const showMessageField = selectedStatus === MeetingServiceOrderStatus.REJECTED
    const isSomeOrderDisabled = orders.some(
        (order) => !!order.orderAction.statusTransitions?.disallowed?.[selectedStatus],
    )

    const {
        mutate: batchUpdate,
        data,
        isLoading,
        isSuccess,
    } = useApiMutation(batchUpdateDeliveryStatus, {
        async onMutate(params) {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            // await queryClient.cancelQueries({
            //     queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
            // })

            // Snapshot the previous value
            const previousData = queryClient.getQueryData<MeetingServiceOrdersResponse>([
                MerchantMeetingServiceOrdersQuery.MEETING_SERVICE,
                meetingServiceDependencies,
            ])?.orders

            queryClient.setQueryData<MeetingServiceOrdersResponse>(
                [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE, meetingServiceDependencies],
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        orders: old.orders.map((order) =>
                            params.updateDeliveryStatus.orderIds.includes(order.id)
                                ? {
                                      ...order,
                                      deliveryDetails: {
                                          ...order.deliveryDetails,
                                          statusDetails: {
                                              ...order.deliveryDetails.statusDetails,
                                              status: params.updateDeliveryStatus.status,
                                              message:
                                                  params.updateDeliveryStatus.message ||
                                                  order.deliveryDetails.statusDetails.message,
                                          },
                                      },
                                  }
                                : order,
                        ),
                    }
                },
            )

            return { previousData }
        },
        async onError(params, _err, context) {
            if ("previousData" in context && context.previousData) {
                context.queryClient.setQueryData<MeetingServiceOrdersResponse>(
                    [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE, meetingServiceDependencies],
                    (old) => {
                        if (!old) return old
                        return {
                            ...old,
                            orders: context.previousData!,
                        }
                    },
                )
            }
            const failedCount = params.updateDeliveryStatus.orderIds.length
            enqueueSnackbar({
                variant: "error",
                message: l10n.getString("meeting-service-batch-modal-batch-error", {
                    ordersCount: failedCount,
                }),
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "center",
                },
            })
        },
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantHomeQuery.MEETING_SERVICE_ORDER_SUMMARY_STATUS],
            })

            const successCount = data?.orders?.filter((tx) => tx.status.code === 200).length ?? 0
            const failedCount = data?.orders?.filter((transaction) => transaction.status.code !== 200).length

            if (failedCount === 0) {
                setShowUndo(successCount)
                onClose()
            } else {
                // Partial failure - refetch to get the accurate state
                await context.queryClient.invalidateQueries({
                    queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
                })
                enqueueSnackbar({
                    variant: "error",
                    message: l10n.getString("meeting-service-batch-modal-batch-error", {
                        ordersCount: failedCount,
                    }),
                    anchorOrigin: {
                        vertical: "top",
                        horizontal: "center",
                    },
                })
            }
        },
        // Always refetch after error or success:
        async onSettled(_data, _error, _params, context) {
            await Promise.all([
                context.queryClient.invalidateQueries({
                    queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE],
                }),
                isTakeaway &&
                    context.queryClient.invalidateQueries({
                        queryKey: [MerchantProductionTransactions.GET_ALL_ORDERS_BY_PERIOD],
                    }),
            ])
        },
    })

    const handleBatchClick = () => {
        batchUpdate({
            updateDeliveryStatus: {
                orderIds: allowedOrderIds,
                status: selectedStatus,
                message: fieldRef?.current?.value,
            },
            isTakeaway,
        })
    }

    return (
        <Stack direction="column" gap={7.5} className={classes.container}>
            {showMessageField && (
                <Box className={classes.messageSection}>
                    <LocalizedStrict id="meeting-service-batch-modal-customer-message">
                        <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                            Message to customer
                        </Typography>
                    </LocalizedStrict>
                    <TextField
                        multiline
                        inputRef={fieldRef}
                        rows={2}
                        fullWidth
                        placeholder={l10n.getString("meeting-service-batch-modal-cancellation-reason-message")}
                        variant="outlined"
                    />
                </Box>
            )}

            <Box>
                {isSomeOrderDisabled && (
                    <Stack direction="row" alignItems="center">
                        <FontAwesomeIcon icon={faCircleExclamation} className={classes.warningIcon} />
                        <LocalizedStrict id="meeting-service-batch-modal-warning-message">
                            <Typography variant="body1">Could not change status for orders with this icon</Typography>
                        </LocalizedStrict>
                    </Stack>
                )}
                <OrdersStatusTable orders={orders} status={selectedStatus} batchResponse={data?.orders} />
            </Box>
            <LocalizedStrict id="meeting-service-batch-modal-button-close-tooltip-label" attrs={{ title: true }}>
                <Tooltip title="No orders can change status">
                    <Box className={classes.changeButton}>
                        <LocalizedStrict
                            id={
                                isSuccess
                                    ? "meeting-service-batch-modal-button-close-label"
                                    : "meeting-service-batch-modal-button-change-label"
                            }
                        >
                            <StandardLoadingButton
                                color="black"
                                loading={isLoading}
                                fullWidth={false}
                                disabled={isLoading || !allowedOrderIds.length}
                                onClick={isSuccess ? onClose : handleBatchClick}
                            >
                                Confirm
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    </Box>
                </Tooltip>
            </LocalizedStrict>
        </Stack>
    )
}
